/**
 * PATH: packages/pricelist/src/usePricelistWorkbook.ts
 * FILE_ID: PKG-PRICELIST-HOOK-001
 * PURPOSE: State + data orchestration for the universal pricelist workbook —
 *          load sections & items, track dirty rows per section, save via bulk
 *          upsert, run column ops, and import xlsx into pending rows.
 * WHY: Keeps PricelistWorkbook.tsx a thin view under the 300-line cap; all
 *      I/O lives here as named async ops with explicit inputs/outputs.
 * RELATED: createPricelistClient, columns, xlsx, types
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ColumnDef, PricelistClient, PricelistSection } from './types'
import { applyComputed, itemToRow, rowToUpsert, type GridRow } from './columns'
import { validateRows } from './columnRules'
import { mapParsedRow, parseWorkbook, type ParsedSheet } from './xlsx'

export interface UsePricelistWorkbookArgs {
  client: PricelistClient
  pricebookId: string
  editable?: boolean
}

/**
 * Module-level workbook cache (2026-07-15 task 12): the pricelist page
 * unmounts on every SPA tab switch, so without this each revisit refetched
 * sections + items and flashed "no rows". Cache the last known state per
 * pricebook and hydrate the next mount instantly; sections still revalidate
 * in the background. In-memory only (dies on browser reload, which is the
 * one case a fresh load IS expected). ponytail: unbounded per-pricebook map —
 * fine for the handful of books an org has; move to LRU if that changes.
 */
interface WorkbookCacheEntry {
  sections: PricelistSection[]
  rowsBySection: Record<string, GridRow[]>
  activeId: string | null
  at: number
}
const workbookCache = new Map<string, WorkbookCacheEntry>()
const WORKBOOK_CACHE_TTL_MS = 15 * 60_000

/** Test hook: clear the module cache between test cases. */
export function __clearWorkbookCache() {
  workbookCache.clear()
}

/** Sentinel id for the synthetic, editable default ("General") tab. */
export const ALL_ITEMS_SECTION_ID = '__all_items__'
/** Sentinel id for the synthetic, read-only "All sections" overview tab. */
export const ALL_SECTIONS_SECTION_ID = '__all_sections__'
/** Name of the real section that adopts ungrouped items on first save. */
export const DEFAULT_SECTION_NAME = 'General'

/**
 * Build the synthetic, read-only "All sections" overview tab — the default
 * landing view. It aggregates the rows of every real section (all suppliers)
 * into one list; it owns no server row and is never editable.
 */
export function buildAllSectionsSection(pricebookId: string, itemCount: number): PricelistSection {
  return {
    id: ALL_SECTIONS_SECTION_ID,
    pricebook: pricebookId,
    name: 'All sections',
    code: '',
    position: -1,
    column_schema: [],
    item_count: itemCount,
    aggregate: true,
  }
}

/**
 * True while `active` still awaits item rows it is KNOWN to have (item_count
 * > 0 but nothing in rowsBySection yet) — so the view can show "Loading rows…"
 * instead of AG-Grid's misleading "No rows to show" flash (task 12). Pure I→O.
 */
export function sectionRowsPending(
  active: PricelistSection | null,
  sections: PricelistSection[],
  rowsBySection: Record<string, unknown[]>,
): boolean {
  if (!active) return false
  if (active.aggregate) {
    return sections.some(
      (s) => !s.aggregate && !s.synthetic && (s.item_count ?? 0) > 0 && !rowsBySection[s.id],
    )
  }
  if (active.synthetic) return false
  return (active.item_count ?? 0) > 0 && !rowsBySection[active.id]
}

/** Non-empty sections keep their order; empty (0-item) sections sink to the end. */
export function orderSectionsEmptyLast(sections: PricelistSection[]): PricelistSection[] {
  const nonEmpty = sections.filter((s) => (s.item_count ?? 0) > 0)
  const empty = sections.filter((s) => (s.item_count ?? 0) === 0)
  return [...nonEmpty, ...empty]
}

/**
 * Build the synthetic default section shown when a pricebook has NO real
 * sections but DOES have (legacy/flat, section=NULL) items — so the universal
 * grid never reads as empty when items exist. It is EDITABLE; the first save
 * (or adding a real section) adopts these items into a real "General" section.
 */
export function buildAllItemsSection(pricebookId: string, itemCount: number): PricelistSection {
  return {
    id: ALL_ITEMS_SECTION_ID,
    pricebook: pricebookId,
    name: DEFAULT_SECTION_NAME,
    code: '',
    position: 0,
    column_schema: [],
    item_count: itemCount,
    synthetic: true,
  }
}

export function usePricelistWorkbook({ client, pricebookId, editable = true }: UsePricelistWorkbookArgs) {
  // Hydrate instantly from the module cache (tab revisit), then revalidate.
  const cachedEntry = workbookCache.get(pricebookId)
  const cached =
    cachedEntry && Date.now() - cachedEntry.at < WORKBOOK_CACHE_TTL_MS ? cachedEntry : null
  const [sections, setSections] = useState<PricelistSection[]>(cached?.sections ?? [])
  const [activeId, setActiveId] = useState<string | null>(cached?.activeId ?? null)
  const [rowsBySection, setRowsBySection] = useState<Record<string, GridRow[]>>(
    cached?.rowsBySection ?? {},
  )
  const [loading, setLoading] = useState(!cached)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const deletedRef = useRef<Record<string, string[]>>({})

  const activeSection = useMemo(
    () => sections.find((s) => s.id === activeId) ?? null,
    [sections, activeId],
  )

  const loadAll = useCallback(async () => {
    // Cached hydration = background revalidate: keep the grid on screen
    // instead of swapping it for the full-page spinner (task 12).
    if (!workbookCache.has(pricebookId)) setLoading(true)
    setError(null)
    try {
      const secs = await client.listSections(pricebookId)
      if (secs.length === 0) {
        // Legacy/flat pricebook: no sections, but there may be ungrouped items
        // (section=NULL). Surface them in an EDITABLE default tab so the grid
        // isn't empty; the first save adopts them into a real section.
        const items = await client.listItems(pricebookId)
        if (items.length > 0) {
          const synthetic = buildAllItemsSection(pricebookId, items.length)
          setSections([synthetic])
          setRowsBySection({ [synthetic.id]: items.map((it) => applyComputed(itemToRow(it), [])) })
          setActiveId(synthetic.id)
          return
        }
      }
      // Real sections: sink empty tabs to the end, and lead with a synthetic
      // read-only "All sections" overview (all suppliers) that is the default
      // landing view — so a freshly-emptied first section never reads as empty.
      const ordered = orderSectionsEmptyLast(secs)
      const total = secs.reduce((n, s) => n + (s.item_count ?? 0), 0)
      const aggregate = buildAllSectionsSection(pricebookId, total)
      setSections([aggregate, ...ordered])
      setActiveId((prev) => prev ?? aggregate.id)
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to load pricelist')
    } finally {
      setLoading(false)
    }
  }, [client, pricebookId])

  const loadSectionItems = useCallback(
    async (sectionId: string, schema: ColumnDef[]) => {
      const items = await client.listItems(pricebookId, sectionId)
      setRowsBySection((prev) => ({
        ...prev,
        [sectionId]: items.map((it) => applyComputed(itemToRow(it), schema)),
      }))
    },
    [client, pricebookId],
  )

  const preloadSections = useCallback(
    async (sectionIds: string[]) => {
      const missing = sectionIds
        .map((id) => sections.find((s) => s.id === id))
        .filter((section): section is PricelistSection => !!section && !rowsBySection[section.id])
      if (!missing.length) return
      const loaded = await Promise.all(
        missing.map(async (section) => {
          const items = await client.listItems(pricebookId, section.id)
          return {
            section,
            rows: items.map((it) => applyComputed(itemToRow(it), section.column_schema || [])),
          }
        }),
      )
      setRowsBySection((prev) => {
        const next = { ...prev }
        for (const { section, rows } of loaded) next[section.id] = rows
        return next
      })
    },
    [client, pricebookId, rowsBySection, sections],
  )

  useEffect(() => {
    loadAll()
  }, [loadAll])

  // Write-through: persist the latest workbook state so the next mount of
  // this pricebook (tab revisit) hydrates instantly with the same tab open.
  useEffect(() => {
    if (!sections.length) return
    workbookCache.set(pricebookId, { sections, rowsBySection, activeId, at: Date.now() })
  }, [pricebookId, sections, rowsBySection, activeId])

  useEffect(() => {
    // Skip the synthetic aggregate tab: it owns no server row (loading it would
    // hit the API with a sentinel id); its rows come from the real sections.
    if (activeSection && !activeSection.aggregate && !rowsBySection[activeSection.id]) {
      loadSectionItems(activeSection.id, activeSection.column_schema || []).catch((e) =>
        setError((e as Error)?.message ?? 'Failed to load items'),
      )
    }
  }, [activeSection, rowsBySection, loadSectionItems])

  // "All sections" overview: preload every real section so the aggregated grid
  // shows all suppliers' rows at once (each section is fetched once, then cached
  // in rowsBySection and reused by its own tab).
  useEffect(() => {
    if (activeId !== ALL_SECTIONS_SECTION_ID) return
    const ids = sections.filter((s) => !s.aggregate).map((s) => s.id)
    if (ids.length) {
      void preloadSections(ids).catch((e) =>
        setError((e as Error)?.message ?? 'Failed to load items'),
      )
    }
  }, [activeId, sections, preloadSections])

  const editCell = useCallback(
    (sectionId: string, rowId: string, field: string, value: unknown) => {
      const schema = sections.find((s) => s.id === sectionId)?.column_schema || []
      setRowsBySection((prev) => {
        const rows = prev[sectionId] || []
        const next = rows.map((r) =>
          r.id === rowId ? applyComputed({ ...r, [field]: value, __dirty: true }, schema) : r,
        )
        return { ...prev, [sectionId]: next }
      })
    },
    [sections],
  )

  /**
   * Adopt the synthetic default tab's ungrouped items into a REAL "General"
   * section: create the section, then bulk-upsert every current row. The
   * sectional upsert matches by id and sets item.section, so existing flat
   * items are reassigned (no duplicates). Returns the created section.
   */
  const adoptFlatItems = useCallback(async (): Promise<PricelistSection> => {
    const general = await client.createSection(pricebookId, {
      name: DEFAULT_SECTION_NAME,
      code: 'general',
      column_schema: [],
      position: 0,
    })
    const rows = rowsBySection[ALL_ITEMS_SECTION_ID] || []
    const res = await client.bulkUpsert(
      pricebookId,
      general.id,
      rows.map((r) => rowToUpsert(r, [])),
      deletedRef.current[ALL_ITEMS_SECTION_ID] || [],
    )
    delete deletedRef.current[ALL_ITEMS_SECTION_ID]
    setRowsBySection((prev) => {
      const next = { ...prev }
      delete next[ALL_ITEMS_SECTION_ID]
      next[general.id] = res.items.map((it) => applyComputed(itemToRow(it), []))
      return next
    })
    return general
  }, [client, pricebookId, rowsBySection])

  const addSection = useCallback(
    async (name: string, columns: ColumnDef[]) => {
      const trimmed = name.trim()
      if (!trimmed) return
      const code = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'section'
      setError(null)
      try {
        const hasSynthetic = sections.some((s) => s.id === ALL_ITEMS_SECTION_ID)
        // Preserve ungrouped items: adopt them into a real "General" section
        // before adding the user's new tab, so they don't vanish from the grid.
        const adopted = hasSynthetic ? await adoptFlatItems() : null
        const realSections = sections.filter((s) => s.id !== ALL_ITEMS_SECTION_ID)
        const created = await client.createSection(pricebookId, {
          name: trimmed,
          code,
          column_schema: columns,
          position: realSections.length + (adopted ? 1 : 0),
        })
        setSections([...(adopted ? [adopted] : []), ...realSections, created])
        setActiveId(created.id)
        return created
      } catch (e) {
        setError((e as Error)?.message ?? 'Failed to create section')
        throw e
      }
    },
    [client, pricebookId, sections, adoptFlatItems],
  )

  const addRow = useCallback((sectionId: string) => {
    const id = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    setRowsBySection((prev) => ({
      ...prev,
      [sectionId]: [
        ...(prev[sectionId] || []),
        { id, __new: true, __dirty: true, item_name: '', is_active: true },
      ],
    }))
  }, [])

  const removeRow = useCallback((sectionId: string, rowId: string) => {
    setRowsBySection((prev) => {
      const rows = prev[sectionId] || []
      const target = rows.find((r) => r.id === rowId)
      if (target && !target.__new) {
        deletedRef.current[sectionId] = [...(deletedRef.current[sectionId] || []), rowId]
      }
      return { ...prev, [sectionId]: rows.filter((r) => r.id !== rowId) }
    })
  }, [])

  /**
   * Insert a blank draft row at a specific index (right-click "insert
   * above/below"). `index` is clamped into range, so callers can pass
   * targetIndex (above) or targetIndex+1 (below) without bounds-checking.
   * Same staged-save semantics as `addRow` (a new __dirty row persisted on
   * the next Save).
   */
  const insertRowAt = useCallback((sectionId: string, index: number) => {
    const id = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    setRowsBySection((prev) => {
      const rows = prev[sectionId] || []
      const at = Math.max(0, Math.min(index, rows.length))
      const next = [...rows]
      next.splice(at, 0, { id, __new: true, __dirty: true, item_name: '', is_active: true })
      return { ...prev, [sectionId]: next }
    })
  }, [])

  /**
   * Duplicate an existing row directly below it (right-click "duplicate row").
   * Copies every DATA column but strips the identity/persistence markers so the
   * clone is a brand-new draft (its own id, __new, __dirty) rather than a second
   * reference to the source item. Computed columns are recomputed via
   * applyComputed so the clone is internally consistent before save.
   */
  const duplicateRow = useCallback(
    (sectionId: string, rowId: string) => {
      const schema = sections.find((s) => s.id === sectionId)?.column_schema || []
      setRowsBySection((prev) => {
        const rows = prev[sectionId] || []
        const idx = rows.findIndex((r) => r.id === rowId)
        if (idx === -1) return prev
        const { id: _id, __new: _new, __dirty: _dirty, ...data } = rows[idx]
        const clone = applyComputed(
          {
            ...data,
            id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            __new: true,
            __dirty: true,
          } as GridRow,
          schema,
        )
        const next = [...rows]
        next.splice(idx + 1, 0, clone)
        return { ...prev, [sectionId]: next }
      })
    },
    [sections],
  )

  const saveSection = useCallback(
    async (sectionId: string) => {
      const section = sections.find((s) => s.id === sectionId)
      if (!section) return
      // Synthetic default tab: first save creates a real "General" section and
      // adopts every ungrouped item into it (no dirty-only filter — we move the
      // whole bucket so it persists and the tab becomes a normal editable one).
      if (section.id === ALL_ITEMS_SECTION_ID) {
        setSaving(true)
        setError(null)
        try {
          const general = await adoptFlatItems()
          setSections([general])
          setActiveId(general.id)
          return
        } catch (e) {
          setError((e as Error)?.message ?? 'Save failed')
          throw e
        } finally {
          setSaving(false)
        }
      }
      const rows = rowsBySection[sectionId] || []
      const dirty = rows.filter((r) => r.__dirty)
      const deleteIds = deletedRef.current[sectionId] || []
      if (!dirty.length && !deleteIds.length) return
      // Rules gate: never push rows that violate the section's column rules
      // (required / numeric min-max). Computed columns are skipped by validateRows.
      const validationErrors = validateRows(dirty, section.column_schema || [])
      if (validationErrors.length) {
        const first = validationErrors.slice(0, 3).map((e) => e.message).join('; ')
        setError(
          `Fix ${validationErrors.length} validation issue(s) before saving: ${first}` +
            (validationErrors.length > 3 ? '…' : ''),
        )
        return
      }
      setSaving(true)
      setError(null)
      try {
        const res = await client.bulkUpsert(
          pricebookId,
          sectionId,
          dirty.map((r) => rowToUpsert(r, section.column_schema || [])),
          deleteIds,
        )
        deletedRef.current[sectionId] = []
        setRowsBySection((prev) => ({
          ...prev,
          [sectionId]: res.items.map((it) => applyComputed(itemToRow(it), section.column_schema || [])),
        }))
        return res
      } catch (e) {
        setError((e as Error)?.message ?? 'Save failed')
        throw e
      } finally {
        setSaving(false)
      }
    },
    [client, pricebookId, sections, rowsBySection, adoptFlatItems],
  )

  const runColumnOp = useCallback(
    async (op: Promise<PricelistSection>) => {
      const updated = await op
      setSections((prev) => prev.map((s) => (s.id === updated.id ? updated : s)))
      await loadSectionItems(updated.id, updated.column_schema || [])
      return updated
    },
    [loadSectionItems],
  )

  const importFile = useCallback(
    async (sectionId: string, buf: ArrayBuffer) => {
      const section = sections.find((s) => s.id === sectionId)
      if (!section) return
      const parsed: ParsedSheet[] = parseWorkbook(buf)
      const sheet = parsed.find((p) => p.name === section.name) ?? parsed[0]
      if (!sheet) return
      const labelToKey: Record<string, string> = {
        Code: 'item_code',
        Description: 'item_name',
        Unit: 'unit',
        Cost: 'base_cost',
        'Markup %': 'markup_percent',
      }
      for (const col of section.column_schema || []) labelToKey[col.label || col.key] = col.key
      const schema = section.column_schema || []
      setRowsBySection((prev) => ({
        ...prev,
        [sectionId]: sheet.rows
          .map((rec) => mapParsedRow(rec, labelToKey))
          .filter((r) => String(r.item_name ?? '').trim())
          .map((r, i) =>
            applyComputed(
              { ...r, id: `imp-${Date.now()}-${i}`, __new: true, __dirty: true, is_active: true } as GridRow,
              schema,
            ),
          ),
      }))
    },
    [sections],
  )

  // The active tab's rows. For the "All sections" overview, flatten every real
  // section's cached rows into one list, tagging each with its section name so
  // the overview's "Section" column can attribute it.
  const rows = useMemo<GridRow[]>(() => {
    if (activeId === ALL_SECTIONS_SECTION_ID) {
      return sections
        .filter((s) => !s.aggregate)
        .flatMap((s) => (rowsBySection[s.id] || []).map((r) => ({ ...r, __section: s.name })))
    }
    return activeId ? rowsBySection[activeId] || [] : []
  }, [activeId, sections, rowsBySection])

  const rowsLoading = useMemo(
    () => sectionRowsPending(activeSection, sections, rowsBySection),
    [activeSection, sections, rowsBySection],
  )

  return {
    sections,
    activeSection,
    activeId,
    setActiveId,
    rowsBySection,
    rows,
    loading,
    rowsLoading,
    saving,
    error,
    editable,
    /**
     * Cells/rows are editable whenever a real/synthetic-default section is
     * active. The read-only "All sections" overview is never editable (edits
     * happen in the individual section tabs).
     */
    activeEditable: editable && !!activeSection && !activeSection.aggregate,
    /** Column ops need a REAL section (synthetic/aggregate have no server row). */
    canAddColumn:
      editable && !!activeSection && !activeSection.synthetic && !activeSection.aggregate,
    /** True while showing the synthetic default tab (pre-adoption). */
    isSyntheticActive: activeSection?.synthetic === true,
    /** True while showing the read-only "All sections" overview tab. */
    isAggregateActive: activeSection?.aggregate === true,
    reload: loadAll,
    /** Refetch one section's item rows (e.g. after a server-side bulk write). */
    reloadSection: useCallback(
      (sectionId: string) =>
        loadSectionItems(
          sectionId,
          sections.find((s) => s.id === sectionId)?.column_schema || [],
        ),
      [loadSectionItems, sections],
    ),
    editCell,
    addSection,
    addRow,
    insertRowAt,
    duplicateRow,
    removeRow,
    saveSection,
    runColumnOp,
    preloadSections,
    importFile,
  }
}
